/**
 * @zendir/ui - ThermalHeatmapCard Component
 *
 * Spacecraft thermal visualization with "paper doll" view showing
 * temperature of each component with color-coded ranges.
 *
 * AstroUXDS Compliance:
 * - Uses status colors appropriately (temperature = status indicator)
 * - Standby (cyan) = Too cold
 * - Normal (green) = Within cool range
 * - Caution (yellow) = Within warm range
 * - Critical (red) = Too hot
 * - Heater indicators use caution color per status semantics
 *
 * Features:
 * - Full null-safety with graceful fallbacks
 * - Color-coded temperature display
 * - Heater status indicators
 * - Loading and empty states
 */

import React, { memo, useMemo } from "react";
import { useTheme } from "../theme";
import {
  safeNumber,
  formatTemperature,
  classNames,
  type StatusLevel,
} from "../utils";

export interface ComponentThermal {
  id: string;
  name: string;
  /** Temperature in Celsius */
  temperatureCelsius: number;
  /** Minimum safe temperature */
  minSafeCelsius: number;
  /** Maximum safe temperature */
  maxSafeCelsius: number;
  /** Heater active status */
  heaterActive?: boolean;
}

export interface ThermalHeatmapCardProps {
  /** Component thermal data */
  components?: ComponentThermal[];
  /** Average spacecraft temperature */
  averageTemperature?: number;
  /** Compact mode (fewer items) */
  compact?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Custom class name */
  className?: string;
}

/**
 * Get status text and level
 */
function getThermalStatus(
  temp: number | undefined,
  min: number,
  max: number,
): { text: string; level: StatusLevel } {
  if (temp === undefined || !Number.isFinite(temp))
    return { text: "--", level: "off" };
  if (temp < min) return { text: "COLD", level: "standby" };
  if (temp > max) return { text: "HOT", level: "critical" };
  return { text: "OK", level: "normal" };
}

/**
 * ThermalHeatmapCard - Displays spacecraft thermal status
 *
 * @example
 * ```tsx
 * <ThermalHeatmapCard
 *   components={thermalComponents}
 *   averageTemperature={22.5}
 * />
 * ```
 */
export const ThermalHeatmapCard = memo(function ThermalHeatmapCard({
  components,
  averageTemperature,
  compact = false,
  loading = false,
  className = "",
}: ThermalHeatmapCardProps): React.ReactElement {
  const { tokens, theme, prefersReducedMotion } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  // Use transparent background with blur for transparent themes (matching Timeline)
  const cardBg = isTransparentTheme
    ? "transparent"
    : tokens.colors.background.surface;
  const cardGlass = isTransparentTheme
    ? {
        backdropFilter: "blur(12px)" as const,
        WebkitBackdropFilter: "blur(12px)" as const,
      }
    : {};

  // Sort components by name
  const sortedComponents = useMemo(() => {
    if (!components) return [];
    return [...components].sort((a, b) => a.name.localeCompare(b.name));
  }, [components]);

  // Calculate overall status
  const hasWarning = useMemo(() => {
    if (!components) return false;
    return components.some((c) => {
      const temp = c.temperatureCelsius;
      return (
        Number.isFinite(temp) &&
        (temp < c.minSafeCelsius || temp > c.maxSafeCelsius)
      );
    });
  }, [components]);

  // Count active heaters
  const activeHeaterCount = useMemo(() => {
    if (!components) return 0;
    return components.filter((c) => c.heaterActive).length;
  }, [components]);

  const transitionDuration = prefersReducedMotion ? "0ms" : "200ms";
  // Medium-soft status colors (readable, not too pale)
  const statusColors: Record<StatusLevel, string> = {
    normal: "#6ed86e",
    caution: "#ffd54f",
    serious: "#ffa726",
    critical: "#e57373",
    off: "#90a4ae",
    standby: "#4fc3f7",
  };

  // Loading state
  if (loading) {
    return (
      <div
        className={classNames(
          "zendir-thermal-heatmap-card",
          "loading",
          className,
        )}
        role="article"
        aria-busy="true"
        aria-label="Loading thermal data"
        style={{
          backgroundColor: cardBg,
          ...cardGlass,
          border: "none",
          borderRadius: tokens.borderRadius.lg,
          padding: tokens.spacing.md,
          minHeight: 200,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              height: 24,
              width: "40%",
              backgroundColor: "rgba(255,255,255,0.1)",
              borderRadius: 4,
            }}
          />
          <div
            style={{
              height: 24,
              width: 60,
              backgroundColor: "rgba(255,255,255,0.1)",
              borderRadius: 4,
            }}
          />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 8,
          }}
        >
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              style={{
                height: 64,
                backgroundColor: "rgba(255,255,255,0.06)",
                borderRadius: 8,
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (!components || components.length === 0) {
    return (
      <div
        className={classNames(
          "zendir-thermal-heatmap-card",
          "empty",
          className,
        )}
        role="article"
        aria-label="No thermal data"
        style={{
          backgroundColor: cardBg,
          ...cardGlass,
          border: "none",
          borderRadius: tokens.borderRadius.lg,
          padding: tokens.spacing.lg,
          textAlign: "center",
          color: tokens.colors.text.tertiary,
        }}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="currentColor"
          style={{ opacity: 0.5, marginBottom: 8 }}
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.5 2C12.433 2 14 3.567 14 5.5L14.0005 12.2576C15.2217 13.2664 16 14.7923 16 16.5C16 19.5376 13.5376 22 10.5 22C7.46243 22 5 19.5376 5 16.5C5 14.7919 5.77868 13.2656 7.00044 12.2568L7 5.5C7 3.567 8.567 2 10.5 2ZM10.5 4C9.7203 4 9.07955 4.59489 9.00687 5.35543L9 5.49987L9.0005 13.199L8.27384 13.799C7.47135 14.4616 7 15.4402 7 16.5C7 18.433 8.567 20 10.5 20C12.433 20 14 18.433 14 16.5C14 15.5111 13.5896 14.593 12.8828 13.9362L12.7268 13.7995L12.0006 13.1997L12 5.5C12 4.7203 11.4051 4.07955 10.6445 4.00687L10.5 4ZM11 5.5C11 5.22386 10.7761 5 10.5 5C10.2239 5 10 5.22386 10 5.5L10.0006 14.0499C8.85917 14.2813 8 15.2903 8 16.5C8 17.8807 9.11929 19 10.5 19C11.8807 19 13 17.8807 13 16.5C13 15.2907 12.1413 14.2819 11.0004 14.0501L11 5.5ZM20 11C20.5523 11 21 11.4477 21 12C21 12.5523 20.5523 13 20 13H17C16.4477 13 16 12.5523 16 12C16 11.4477 16.4477 11 17 11H20ZM19 9C19 8.44772 18.5523 8 18 8H17C16.4477 8 16 8.44772 16 9C16 9.55228 16.4477 10 17 10H18C18.5523 10 19 9.55228 19 9ZM20 5C20.5523 5 21 5.44772 21 6C21 6.55228 20.5523 7 20 7H17C16.4477 7 16 6.55228 16 6C16 5.44772 16.4477 5 17 5H20ZM19 3C19 2.44772 18.5523 2 18 2H17C16.4477 2 16 2.44772 16 3C16 3.55228 16.4477 4 17 4H18C18.5523 4 19 3.55228 19 3Z"
          />
        </svg>
        <p style={{ margin: 0, fontSize: tokens.typography.body[2].fontSize }}>
          No thermal data available
        </p>
      </div>
    );
  }

  const getTempColor = (temp: number | undefined, min: number, max: number) => {
    if (temp === undefined || !Number.isFinite(temp)) return statusColors.off;
    const mid = (max + min) / 2;
    if (temp < min) return statusColors.standby;
    if (temp > max) return statusColors.critical;
    if (temp > mid + (max - mid) * 0.5) return statusColors.caution;
    return statusColors.normal;
  };
  const overallStatusColor = hasWarning
    ? statusColors.critical
    : statusColors.normal;

  return (
    <article
      className={classNames("zendir-thermal-heatmap-card", className)}
      aria-label="Spacecraft thermal status"
      style={{
        backgroundColor: cardBg,
        ...cardGlass,
        border: "none",
        borderRadius: tokens.borderRadius.lg,
        padding: tokens.spacing.md,
        fontFamily: tokens.typography.fontFamily.primary,
        color: tokens.colors.text.primary,
      }}
    >
      {/* Header */}
      {!compact && (
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: tokens.spacing.md,
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: tokens.typography.fontSize.md,
                fontWeight: tokens.typography.fontWeight.semibold,
              }}
            >
              Thermal Status
            </h3>
            <p
              style={{
                margin: "2px 0 0 0",
                fontSize: tokens.typography.fontSize.xs,
                color: tokens.colors.text.tertiary,
              }}
            >
              {components.length} zones • Avg{" "}
              <span style={{ fontFamily: tokens.typography.fontFamily.mono }}>
                {formatTemperature(averageTemperature)}
              </span>
            </p>
          </div>
          <span
            role="status"
            style={{
              padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
              borderRadius: tokens.borderRadius.sm,
              fontSize: tokens.typography.fontSize.xxs, // 0.625rem / 10px (AstroUXDS compact)
              fontWeight: tokens.typography.fontWeight.medium, // 500 (AstroUXDS medium)
              backgroundColor: `${overallStatusColor}20`,
              color: overallStatusColor,
            }}
          >
            {hasWarning ? "Warning" : "Nominal"}
          </span>
        </header>
      )}

      {/* Thermal Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: tokens.spacing.sm,
        }}
      >
        {sortedComponents.slice(0, compact ? 6 : 9).map((component) => {
          const color = getTempColor(
            component.temperatureCelsius,
            component.minSafeCelsius,
            component.maxSafeCelsius,
          );
          const status = getThermalStatus(
            component.temperatureCelsius,
            component.minSafeCelsius,
            component.maxSafeCelsius,
          );

          return (
            <div
              key={component.id}
              style={{
                position: "relative",
                borderRadius: tokens.borderRadius.md,
                padding: tokens.spacing.sm,
                textAlign: "center",
                transition: `transform ${transitionDuration}`,
                backgroundColor: `${color}22`,
                borderLeft: `3px solid ${color}`,
              }}
            >
              {/* Heater indicator */}
              {component.heaterActive && (
                <div
                  title="Heater Active"
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    width: 8,
                    height: 8,
                    backgroundColor: statusColors.caution,
                    borderRadius: "50%",
                    animation: prefersReducedMotion
                      ? "none"
                      : "heaterPulse 1s ease-in-out infinite",
                  }}
                />
              )}

              {/* Component name */}
              <div
                style={{
                  fontSize: tokens.typography.fontSize.xxs, // 0.625rem / 10px (AstroUXDS compact)
                  color: tokens.colors.text.tertiary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  marginBottom: 2,
                }}
              >
                {component.name}
              </div>

              {/* Temperature */}
              <div
                style={{
                  fontSize: tokens.typography.fontSize.lg,
                  fontWeight: tokens.typography.fontWeight.bold,
                  fontFamily: tokens.typography.fontFamily.mono,
                  fontVariantNumeric: "tabular-nums",
                  color,
                }}
              >
                {safeNumber(component.temperatureCelsius, 0)}°
              </div>

              {/* Status */}
              <div
                style={{
                  fontSize: tokens.typography.fontSize.micro, // 0.5625rem / 9px (AstroUXDS micro)
                  fontWeight: tokens.typography.fontWeight.medium, // 500 (AstroUXDS medium)
                  color: statusColors[status.level] || statusColors.off,
                }}
              >
                {status.text}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div
        style={{
          marginTop: tokens.spacing.md,
          paddingTop: tokens.spacing.md,
          borderTop: `1px solid ${tokens.colors.border.muted}`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: tokens.typography.fontSize.xxs, // 0.625rem / 10px (AstroUXDS compact)
          }}
        >
          <span style={{ color: statusColors.standby }}>Cold</span>
          <div
            style={{
              flex: 1,
              margin: `0 ${tokens.spacing.sm}`,
              height: 8,
              borderRadius: 4,
              background: `linear-gradient(to right, ${statusColors.standby}, ${statusColors.normal}, ${statusColors.caution}, ${statusColors.critical})`,
            }}
          />
          <span style={{ color: statusColors.critical }}>Hot</span>
        </div>

        {/* Active heaters */}
        {activeHeaterCount > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: tokens.typography.fontSize.xs,
              marginTop: tokens.spacing.sm,
            }}
          >
            <span style={{ color: tokens.colors.text.tertiary }}>
              Active Heaters
            </span>
            <span
              style={{
                color: statusColors.caution,
                fontFamily: tokens.typography.fontFamily.mono,
              }}
            >
              {activeHeaterCount}/{components.length}
            </span>
          </div>
        )}
      </div>

      {/* Keyframe animation */}
      <style>{`
        @keyframes heaterPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </article>
  );
});
